import { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { fmtUsd } from "@/lib/format";

interface DayPoint {
  day: string;
  total: number;
  cumulative: number;
  grid: number;
  scalp: number;
  inv_swing: number;
  alpha: number;
  hourly: number;
}

interface Props {
  days: DayPoint[];
  height?: number;
  showAxis?: boolean;
}

/**
 * ProfitMotivation — the "you ARE making money" chart.
 *
 * Plots cumulative realized PnL across ALL engines (grid + scalp + inventory
 * swing + alpha + hourly). Area is green when profitable, red when underwater.
 * This is the dashboard's emotional anchor: a single line that goes up means
 * the bot is winning across the portfolio of strategies, even when individual
 * positions are red.
 */
export function ProfitMotivation({ days, height = 220, showAxis = true }: Props) {
  const data = useMemo(() => days || [], [days]);
  if (!data.length) {
    return (
      <div className="grid place-items-center text-muted text-sm" style={{ height }}>
        No realized P&L yet — chart appears after the first closed trade.
      </div>
    );
  }
  const final = data[data.length - 1].cumulative;
  const peak = Math.max(...data.map(d => d.cumulative));
  const isProfit = final >= 0;
  const color = isProfit ? "hsl(var(--good))" : "hsl(var(--bad))";

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="profit-mot" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.5} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        {showAxis && (
          <XAxis
            dataKey="day"
            stroke="hsl(var(--muted))"
            fontSize={11}
            tickFormatter={(d: string) => d.slice(5)}
            minTickGap={32}
          />
        )}
        {showAxis && (
          <YAxis
            stroke="hsl(var(--muted))"
            fontSize={11}
            tickFormatter={(v: number) => `$${v.toFixed(2)}`}
            width={55}
          />
        )}
        <Tooltip
          contentStyle={{
            background: "hsl(var(--bg-2))",
            border: "1px solid hsl(var(--border))",
            borderRadius: 6,
            fontSize: 12,
          }}
          formatter={(v: any, name: any) => {
            const n = typeof v === "number" ? v : Number(v);
            const labels: Record<string, string> = {
              cumulative: "Cumulative",
              total: "This day",
              grid: "Grid",
              scalp: "Scalp",
              inv_swing: "Inv-Swing",
              alpha: "Alpha",
              hourly: "Hourly",
            };
            const key = String(name ?? "");
            return [fmtUsd(n), labels[key] || key];
          }}
          labelFormatter={(l) => `Day ${l}`}
        />
        <ReferenceLine y={0} stroke="hsl(var(--muted))" strokeDasharray="3 3" />
        {peak > 0 && (
          <ReferenceLine
            y={peak}
            stroke="hsl(var(--accent))"
            strokeDasharray="2 4"
            label={{ value: `peak $${peak.toFixed(2)}`, fontSize: 10, fill: "hsl(var(--accent))", position: "insideTopRight" }}
          />
        )}
        <Area
          type="monotone"
          dataKey="cumulative"
          stroke={color}
          fill="url(#profit-mot)"
          strokeWidth={2.5}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
